/**
 * Instanced ground decals that fade out: shockwave rings, scorch, frost, holy glyph, cracked
 * ground, telegraph "slam" flashes (shape-aware: circle / ring / cone / line / rect) and soft
 * flashes. Two batches share the shader: additive (light) and alpha (darkening: scorch, cracks).
 * They draw just above the floor and below telegraph decals (renderOrder), so danger stays on top.
 */
import * as THREE from 'three';
import type { WorldShape } from '@keystone/sim';
import { groundY } from '../ground';
import { NOISE_GLSL } from '../style';

export const DECAL = { ring: 0, scorch: 1, frost: 2, glyph: 3, cracks: 4, slam: 5, flash: 6 } as const;
export type DecalKind = (typeof DECAL)[keyof typeof DECAL];
const SHAPE = { circle: 0, ring: 1, cone: 2, line: 3, rect: 4 } as const;

const VERT = `
attribute vec4 iOrigin; attribute vec4 iExtent; attribute vec4 iShape; attribute vec4 iColor; attribute vec4 iTime;
varying vec2 vLocal; varying vec4 vShape; varying vec4 vColor; varying vec4 vTime; varying float vShapeType;
void main(){
  vec2 local = vec2(iExtent.x + position.x * iExtent.y * 2.0, position.y * iExtent.z * 2.0);
  vLocal = local; vShape = iShape; vColor = iColor; vTime = iTime; vShapeType = iOrigin.w;
  float c = cos(iOrigin.z), s = sin(iOrigin.z);
  // sim facing: +x forward; sim y maps to -z in three.
  vec2 w = vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  // iExtent.w: floor height at the origin (elevation).
  vec3 pos = vec3(iOrigin.x + w.x, iExtent.w, iOrigin.y - w.y);
  if (iTime.x >= 1.0 || iTime.x < 0.0) pos = vec3(0.0, -50.0, 0.0);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`;

const FRAG = `
uniform float uFx;
varying vec2 vLocal; varying vec4 vShape; varying vec4 vColor; varying vec4 vTime; varying float vShapeType;
${NOISE_GLSL}
void main(){
  float t = vTime.x; if (t >= 1.0) discard;
  float age = vTime.y;
  float A = vShape.x, B = vShape.y, seed = vShape.z; int kind = int(vShape.w + 0.5);
  int sh = int(vShapeType + 0.5);
  vec2 p = vLocal; float d = length(p); float ang = atan(p.y, p.x);
  float norm; float edgeD;
  if (sh == 0) { if (d > A) discard; norm = d / A; edgeD = A - d; }
  else if (sh == 1) { if (d > A || d < B) discard; norm = (d - B) / max(A - B, 0.01); edgeD = min(d - B, A - d); }
  else if (sh == 2) { float aa = abs(ang); if (d > A || aa > B) discard; norm = d / A; edgeD = min(A - d, (B - aa) * d); }
  else if (sh == 3) { if (p.x < 0.0 || p.x > A || abs(p.y) > B * 0.5) discard; norm = p.x / A; edgeD = min(min(p.x, A - p.x), B * 0.5 - abs(p.y)); }
  else { if (abs(p.x) > A * 0.5 || abs(p.y) > B * 0.5) discard; norm = max(abs(p.x) / (A * 0.5), abs(p.y) / (B * 0.5)); edgeD = min(A * 0.5 - abs(p.x), B * 0.5 - abs(p.y)); }
  float fadeOut = 1.0 - smoothstep(0.6, 1.0, t);
  vec3 col = vColor.rgb; float a = 0.0;
  if (kind == 0) {            // expanding shockwave ring
    float e = 1.0 - pow(1.0 - t, 3.0);
    float w = mix(0.16, 0.035, t);
    float band = 1.0 - smoothstep(0.0, w, abs(norm - e));
    float inner = (1.0 - smoothstep(0.0, e, norm)) * 0.12 * (1.0 - t);
    a = (band + inner) * (1.0 - t);
    col = mix(col, vec3(1.0), band * 0.25 * (1.0 - t));
  } else if (kind == 1) {     // scorch: dark burnt blotch, ember rim early
    float n = fbm(p * 0.9 + seed * 7.0);
    float m = 1.0 - smoothstep(0.35 + n * 0.35, 0.95, norm);
    a = m * (0.75 + 0.25 * n) * fadeOut;
    col = vColor.rgb;
  } else if (kind == 2) {     // frost: pale crystalline star
    float spokes = pow(abs(cos(ang * 3.0 + seed * 6.0)), 18.0) * (1.0 - norm);
    float n = fbm(p * 1.6 + seed * 3.0);
    a = (smoothstep(1.0, 0.2, norm) * 0.35 * n + spokes * 0.8) * fadeOut;
  } else if (kind == 3) {     // holy glyph: rotating rings, runes and a hexagram
    float rot = age * 0.35 + seed * 6.28;
    float an = ang + rot;
    float r1 = 1.0 - smoothstep(0.0, 0.1 / A, abs(norm - 0.93));
    float r2 = 1.0 - smoothstep(0.0, 0.07 / A, abs(norm - 0.78));
    // Thin radial rune strokes between the two rings (line width in yards, not a fraction).
    float seg = fract(an * 18.0 / 6.2832) - 0.5;
    float tickW = 0.05 / max(d, 0.5) * 18.0 / 6.2832;
    float ticks = (1.0 - smoothstep(tickW * 0.5, tickW, abs(seg))) * step(0.81, norm) * step(norm, 0.9) * 0.7;
    vec2 q = vec2(cos(an), sin(an)) * d / A;
    float hex = 0.0;
    for (int i = 0; i < 6; i++) {
      float th = float(i) * 1.0472;
      vec2 n2 = vec2(cos(th), sin(th));
      hex = max(hex, 1.0 - smoothstep(0.0, 0.06 / A, abs(dot(q, n2) - 0.39)));
    }
    hex *= step(norm, 0.78);
    float breathe = 0.8 + 0.2 * sin(age * 3.0);
    float grow = smoothstep(0.0, 0.08, t);
    a = (r1 + r2 * 0.7 + ticks + hex * 0.55) * breathe * grow * fadeOut;
  } else if (kind == 4) {     // cracked ground: ridged noise lines, glowing seams
    vec2 q = p * 0.55 + seed * 11.0;
    float r = 1.0 - abs(noise(q) * 2.0 - 1.0);
    float r2 = 1.0 - abs(noise(q * 2.1 + 4.0) * 2.0 - 1.0);
    float crack = smoothstep(0.86, 0.97, max(r, r2 * 0.95));
    float grow = smoothstep(0.0, 0.12, t);
    float m = 1.0 - smoothstep(0.75, 1.0, norm);
    a = crack * m * grow * fadeOut;
    col = mix(vColor.rgb, vColor.rgb * 0.35, crack);
  } else if (kind == 5) {     // telegraph slam: flash fill + outward wave + radial streaks
    float wave = 1.0 - smoothstep(0.0, 0.09, abs(norm - (t * 1.3)));
    float fill = (1.0 - smoothstep(0.0, 0.35, t)) * 0.38;
    float streak = pow(noise(vec2(ang * 9.0 + seed * 20.0, 0.0)), 6.0) * (1.0 - t) * 0.8;
    float edge = (1.0 - smoothstep(0.0, 0.18, edgeD)) * (1.0 - t);
    a = max(max(fill, wave * 0.85 * (1.0 - t)), max(streak * step(0.15, norm), edge * 0.7));
    col = mix(col, vec3(1.0, 0.92, 0.75), wave * 0.3);
  } else {                    // soft flash disc
    a = (1.0 - smoothstep(0.0, 1.0, norm)) * (1.0 - t) * (1.0 - t);
  }
  a *= vColor.a * uFx;
  if (a < 0.004) discard;
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
}`;

export class DecalBatch {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  private readonly origin: Float32Array;
  private readonly extent: Float32Array;
  private readonly shape: Float32Array;
  private readonly color: Float32Array;
  private readonly time: Float32Array;
  private readonly aTime: THREE.InstancedBufferAttribute;
  private readonly statics: THREE.InstancedBufferAttribute[];
  private readonly age: Float32Array;
  private readonly life: Float32Array;
  /** Fixed-life decals expire on their own; held ones stay at `hold` until released. */
  private readonly hold: Float32Array;
  private readonly alive: Uint8Array;
  private readonly gen: Uint32Array;
  private staticDirty = false;
  private hwm = 0;
  /** Max fresh same-kind decals stacked on one spot. */
  maxStack = 3;

  constructor(readonly capacity: number, fx: { value: number }, additive: boolean, renderOrder: number) {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.setAttribute('position', quad.getAttribute('position'));
    const mk = (n: number): [Float32Array, THREE.InstancedBufferAttribute] => {
      const arr = new Float32Array(capacity * n);
      const a = new THREE.InstancedBufferAttribute(arr, n).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
      return [arr, a];
    };
    let a: THREE.InstancedBufferAttribute;
    const sts: THREE.InstancedBufferAttribute[] = [];
    [this.origin, a] = mk(4);
    this.geo.setAttribute('iOrigin', a);
    sts.push(a);
    [this.extent, a] = mk(4);
    this.geo.setAttribute('iExtent', a);
    sts.push(a);
    [this.shape, a] = mk(4);
    this.geo.setAttribute('iShape', a);
    sts.push(a);
    [this.color, a] = mk(4);
    this.geo.setAttribute('iColor', a);
    sts.push(a);
    [this.time, this.aTime] = mk(4);
    this.geo.setAttribute('iTime', this.aTime);
    this.statics = sts;
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.hold = new Float32Array(capacity).fill(-1);
    this.alive = new Uint8Array(capacity);
    this.gen = new Uint32Array(capacity);
    for (let i = 0; i < capacity; i++) this.time[i * 4] = 2;
    this.geo.instanceCount = 0;
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uFx: fx },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.position.y = 0.025;
  }

  /**
   * Spawn a decal. Circle by default (`radius`), or shape-aware with `shape`. `holdAt` (0..1)
   * pins the animation phase until `release` (persistent decals). Returns a handle or -1.
   */
  spawn(kind: DecalKind, x: number, y: number, radius: number, r: number, g: number, b: number, a: number, lifeS: number, shape?: WorldShape, holdAt = -1): number {
    // Overdraw guard: same-kind decals stacking on one spot fade each other out; past
    // `maxStack` fresh ones the new decal is dropped (persistent ones always go through).
    if (holdAt < 0) {
      const n = this.overlapCount(kind, x, y, Math.max(0.6, radius * 0.6));
      if (n >= this.maxStack) return -1;
      a /= 1 + 0.6 * n;
    }
    let i = -1;
    for (let k = 0; k < this.capacity; k++) {
      if (!this.alive[k]) {
        i = k;
        break;
      }
    }
    if (i < 0) {
      // Recycle the most-progressed fixed-life decal.
      let best = -1;
      let bestT = -1;
      for (let k = 0; k < this.capacity; k++) {
        const t = this.hold[k]! >= 0 ? -1 : this.time[k * 4]!;
        if (t > bestT) {
          bestT = t;
          best = k;
        }
      }
      if (best < 0) return -1;
      i = best;
    }
    this.alive[i] = 1;
    this.gen[i] = (this.gen[i]! + 1) & 0x7fff;
    if (i + 1 > this.hwm) this.hwm = i + 1;
    const o = i * 4;
    let shapeType: number = SHAPE.circle;
    let A = radius;
    let B = 0;
    let cx = 0;
    let hx = radius;
    let hz = radius;
    let facing = 0;
    if (shape) {
      facing = shape.facing;
      const s = shape.shape;
      shapeType = SHAPE[s.type];
      switch (s.type) {
        case 'circle':
          A = s.radius;
          hx = hz = s.radius;
          break;
        case 'ring':
          A = s.outer;
          B = s.inner;
          hx = hz = s.outer;
          break;
        case 'cone': {
          A = s.range;
          B = Math.min(Math.PI, s.angle / 2);
          const minX = B > Math.PI / 2 ? -s.range * Math.sin(B - Math.PI / 2) : 0;
          const halfW = B > Math.PI / 2 ? s.range : s.range * Math.sin(B);
          cx = (s.range + minX) / 2;
          hx = (s.range - minX) / 2;
          hz = halfW;
          break;
        }
        case 'line':
          A = s.length;
          B = s.width;
          cx = s.length / 2;
          hx = s.length / 2;
          hz = s.width / 2;
          break;
        case 'rect':
          A = s.h;
          B = s.w;
          hx = s.h / 2;
          hz = s.w / 2;
          break;
      }
    }
    this.origin[o] = x;
    this.origin[o + 1] = -y;
    this.origin[o + 2] = facing;
    this.origin[o + 3] = shapeType;
    this.extent[o] = cx;
    this.extent[o + 1] = hx + 0.05;
    this.extent[o + 2] = hz + 0.05;
    this.extent[o + 3] = groundY(x, y);
    this.shape[o] = A;
    this.shape[o + 1] = B;
    this.shape[o + 2] = (i * 0.618 + x * 0.013) % 1;
    this.shape[o + 3] = kind;
    this.color[o] = r;
    this.color[o + 1] = g;
    this.color[o + 2] = b;
    this.color[o + 3] = a;
    this.age[i] = 0;
    this.life[i] = Math.max(0.05, lifeS);
    this.hold[i] = holdAt;
    this.time[o] = 0;
    this.time[o + 1] = 0;
    this.staticDirty = true;
    return i | (this.gen[i]! << 16);
  }

  /** Fresh (first 60 % of life) same-kind decals within `dist` yards of (x, y). */
  overlapCount(kind: number, x: number, y: number, dist: number): number {
    let n = 0;
    const d2 = dist * dist;
    for (let k = 0; k < this.hwm; k++) {
      if (!this.alive[k] || this.hold[k]! >= 0) continue;
      const o = k * 4;
      if (this.shape[o + 3] !== kind || this.time[o]! > 0.6) continue;
      const dx = this.origin[o]! - x;
      const dz = this.origin[o + 1]! + y;
      if (dx * dx + dz * dz <= d2) n++;
    }
    return n;
  }

  private slot(handle: number): number {
    if (handle < 0) return -1;
    const i = handle & 0xffff;
    return i < this.capacity && this.alive[i] && this.gen[i] === handle >>> 16 ? i : -1;
  }

  /** Let a held decal play out its remaining fade (`fadeS` seconds). */
  release(handle: number, fadeS = 0.5): void {
    const i = this.slot(handle);
    if (i < 0) return;
    const t = Math.max(this.hold[i]!, 0.6);
    this.hold[i] = -1;
    this.life[i] = fadeS / Math.max(0.05, 1 - t);
    this.age[i] = t * this.life[i]!;
  }

  setAlpha(handle: number, a: number): void {
    const i = this.slot(handle);
    if (i < 0) return;
    this.color[i * 4 + 3] = a;
    this.staticDirty = true;
  }

  update(dt: number): void {
    let top = 0;
    for (let i = 0; i < this.hwm; i++) {
      if (!this.alive[i]) continue;
      const o = i * 4;
      this.time[o + 1] = this.time[o + 1]! + dt; // absolute age (animation)
      if (this.hold[i]! >= 0) {
        // Grow in over the first ~0.4 s, then sit at the hold phase.
        this.age[i] = Math.min(this.age[i]! + dt, this.hold[i]! * this.life[i]!);
        this.time[o] = this.age[i]! / this.life[i]!;
        top = i + 1;
        continue;
      }
      this.age[i] = this.age[i]! + dt;
      const t = this.age[i]! / this.life[i]!;
      if (t >= 1) {
        this.alive[i] = 0;
        this.time[o] = 2;
        continue;
      }
      this.time[o] = t;
      top = i + 1;
    }
    this.hwm = top;
    this.geo.instanceCount = this.hwm;
    if (this.hwm > 0) this.aTime.needsUpdate = true;
    if (this.staticDirty) {
      this.staticDirty = false;
      for (const a of this.statics) a.needsUpdate = true;
    }
  }

  clear(): void {
    this.alive.fill(0);
    this.hold.fill(-1);
    for (let i = 0; i < this.capacity; i++) this.time[i * 4] = 2;
    this.hwm = 0;
    this.geo.instanceCount = 0;
    this.aTime.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
